import { Add, UploadFile } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Fade,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";

import DataImport from "@components/common/DataImporter/DataImport";
import { DataImportFieldType } from "@components/common/DataImporter/DataImport.interfaces";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import { useModals } from "../../context/ModalContext";
import { closeContactModalKeys, invalidateContactsAndClients, menuPropsModal } from "../../utils";

import { BaseModalComponent } from "../common/BaseModalComponent";
import { ClientsRow } from "./ClientsRow";
import { PaginationLoadingOverlay } from "./PaginationLoadingOverlay";

import { useBulkImportClientsMutation } from "./hooks/useBulkImportClients.mutation";
import { useClientPagination } from "./hooks/useClientPagination";
import { useClientSearch } from "./hooks/useClientSearch";

import { alphaVar, colors } from "../ui/colors";
import { getFilterPlaceholder } from "./utils";

import { useQueryClient } from "@tanstack/react-query";
import { ClientCreate } from "./ClientCreate";
import { ClientDetailsTabs } from "./ClientDetailsModal/ClientDetailsTabs";
import { useClients } from "./hooks/useClients";
import { usePatchJob } from "./hooks/usePatchJob";

// ─── Constants ────────────────────────────────────────────────────────────────

const GRAY = colors.gray;
const ORANGE = colors.orange;

const TABLE_COLUMNS = [
  { label: "Date", sortKey: "createdAt", hideOnMobile: true },
  { label: "Contact", sortKey: "clientName", hideOnMobile: false },
  { label: "Type", sortKey: null, hideOnMobile: false },
  { label: "Email", sortKey: null, hideOnMobile: false },
  { label: "Phone", sortKey: null, hideOnMobile: true },
];

const CLIENT_IMPORT_FIELDS = [
  {
    key: "firstName",
    label: "First Name",
    required: true,
    fieldType: DataImportFieldType.String,
    aliases: ["nombre", "name", "first_name", "fname"],
  },
  { key: "lastName", label: "Last Name", fieldType: DataImportFieldType.String, aliases: ["apellido", "last_name", "lname"] },
  { key: "email", label: "Email", required: false, fieldType: DataImportFieldType.Email, aliases: ["correo", "e-mail", "mail"] },
  { key: "phone", label: "Phone", fieldType: DataImportFieldType.String, aliases: ["telefono", "mobile", "cellphone"] },
  { key: "address", label: "Address", fieldType: DataImportFieldType.String, aliases: ["direccion", "street"] },
  { key: "city", label: "City", fieldType: DataImportFieldType.String, aliases: ["ciudad"] },
  { key: "state", label: "State", fieldType: DataImportFieldType.String, aliases: ["estado", "province", "region"] },
  { key: "zipCode", label: "Zip Code", fieldType: DataImportFieldType.String, aliases: ["postal code", "codigo postal", "zip"] },
  { key: "clientCompanyName", label: "Company Name", fieldType: DataImportFieldType.String, aliases: ["empresa", "business name", "company"] },
  { key: "type", label: "Client Type", fieldType: DataImportFieldType.String, aliases: ["tipo", "category"] },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function ClientSearchBar({ filterBy, setFilterBy, searchValue, handleSearchChange, handleClearSearch, searchInputRef, setPageToFirst, activeTable }) {
  const basePlaceholder = getFilterPlaceholder(filterBy);
  const placeholder = activeTable === "prospects" ? basePlaceholder.replace(/client/gi, "prospect") : basePlaceholder;

  return (
    <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
      <TextField
        inputRef={searchInputRef}
        size="small"
        placeholder={placeholder}
        value={searchValue}
        onChange={(e) => {
          handleSearchChange(e);
          setPageToFirst();
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          "& .MuiOutlinedInput-root": {
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            border: `1px solid rgba(12,12,12,.06)`,
            boxShadow: "0 2px 8px rgba(12,12,12,.04)",
            borderRadius: "8px 0 0 8px",
            height: 40,
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: GRAY[400], fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: !!searchValue && (
            <Fade in>
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    handleClearSearch();
                    setPageToFirst();
                  }}
                >
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            </Fade>
          ),
        }}
      />

      <FormControl size="small">
        <Select
          MenuProps={menuPropsModal}
          value={filterBy}
          onChange={(e) => {
            setFilterBy(e.target.value);
            setPageToFirst();
          }}
          sx={{
            minWidth: { xs: 70, sm: 120 },
            minHeight: 40,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            borderRadius: "0 8px 8px 0",
            border: `1px solid rgba(12,12,12,.06)`,
            borderLeft: "none",
            "& .MuiSelect-select": { py: "8.5px", fontSize: { xs: "0.75rem", sm: "0.875rem" }, pl: { xs: 1, sm: 2 } },
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          }}
        >
          <MenuItem value="clientName">Name</MenuItem>
          <MenuItem value="email">Email</MenuItem>
          <MenuItem value="phone">Phone</MenuItem>
          <MenuItem value="address">Address</MenuItem>
          <MenuItem value="type">Type</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
}

function ClientTableActions({
  onConfirmImport,
  bulkImportMutation,
  skipClientSelection,
  onSkipClientSelection,
  isUpdateJob,
  isCreateJob,
  selectedClient,
  onUpdateJob,
  onClientCreated,
  updateJobLabel = "Update Job",
  contact = null,
  activeTable,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        justifyContent: { xs: "flex-end", sm: "flex-end" },
      }}
    >
      {isCreateJob && (
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={skipClientSelection} onChange={(_, checked) => onSkipClientSelection?.(checked)} size="small" />}
            label="Skip client selection"
            sx={{
              mr: 0,
              "& .MuiFormControlLabel-label": {
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                whiteSpace: "nowrap",
              },
            }}
          />
        </FormGroup>
      )}

      {isUpdateJob && (
        <Button
          variant="contained"
          disableElevation
          disabled={!selectedClient}
          onClick={onUpdateJob}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            height: 40,
          }}
        >
          {updateJobLabel}
        </Button>
      )}

      <BaseModalComponent
        modalKey="Dashboard-Client-Import"
        modalLabel={activeTable === "prospects" ? "Bulk Import Prospects" : "Bulk Import Clients"}
        defaultSizes={{ width: 900 }}
        elementTrigger={
          <Button
            variant="outlined"
            color="primary"
            startIcon={<UploadFile />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              height: 40,
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Import CSV
            </Box>

            <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
              Import
            </Box>
          </Button>
        }
      >
        <Stack spacing={2}>
          <DataImport
            entityName={activeTable === "prospects" ? "Prospects" : "Clients"}
            fields={CLIENT_IMPORT_FIELDS}
            onConfirm={onConfirmImport}
            isLoading={bulkImportMutation.isPending}
            maxPreviewRows={15}
          />

          {bulkImportMutation.isError && (
            <Alert severity="error" sx={{ borderRadius: 1.5 }}>
              {getBulkImportErrorMessage(bulkImportMutation.error)}
            </Alert>
          )}
        </Stack>
      </BaseModalComponent>

      <BaseModalComponent
        modalKey="Dashboard-ClientAdd-Business"
        modalLabel={activeTable === "prospects" ? "Add Prospect" : "Add Client"}
        defaultSizes={{ width: 800 }}
        allowInsidePreventModal
        elementTrigger={
          <Button
            variant="contained"
            disableElevation
            sx={{
              textTransform: "none",
              bgcolor: ORANGE.base,
              color: "white",
              fontWeight: 600,
              borderRadius: 2,
              height: 40,
              px: { xs: 1.5, sm: 3 },
              "&:hover": {
                bgcolor: ORANGE[600],
              },
            }}
          >
            <Add sx={{ display: { xs: "block", sm: "none" } }} />

            <Box component="span" sx={{ display: { xs: "none", sm: "block" } }}>
              {activeTable === "prospects" ? "+ New prospect" : "+ New client"}
            </Box>
          </Button>
        }
      >
        <ClientCreate contact={contact} status={activeTable === "prospects" ? "PROSPECT" : "ACTIVE"} onCreated={onClientCreated} />
      </BaseModalComponent>
    </Box>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getBulkImportErrorMessage(error) {
  if (!error) return "Something went wrong while importing clients.";

  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.response?.data?.statusMessage ||
    (error instanceof Error && error.message) ||
    "Something went wrong while importing clients."
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * ClientsTable — displays a paginated, searchable, sortable list of clients.
 *
 * @param {number|null}   maxRowPerPage              - Max rows per page. If null, default options are used.
 * @param {object|false}  selectedClient             - Currently selected client, or false.
 * @param {boolean}       disableInnerChilds         - Disables coverage-area expansion inside rows.
 * @param {boolean}       disableRecordClickable     - Prevents opening the details modal on row click.
 * @param {boolean}       allowSelected              - Highlights the selected row.
 * @param {boolean}       disableMenuOptions         - Hides the actions menu column.
 * @param {boolean}       disableClientDetailsModal  - Skips rendering the details modal entirely (avoids dependency cycles).
 * @param {function}      onSelectClientAndCoverage  - Callback when a client and coverage area are selected.
 * @param {string|null}   clientUidFilter            - Filters the table to a single client by UID.
 * @param {boolean}       skipClientSelection        - Skips client selection.
 * @param {function}      onSkipClientSelection      - Callback when client selection is skipped.
 * @param {boolean}       isUpdateJob                - Used in the job create stepper to update the job state.
 * @param {object}        sx                         - MUI sx prop for custom styling.
 */
export const ClientsTable = ({
  maxRowPerPage = null,
  selectedClient = false,
  disableInnerChilds = false,
  disableRecordClickable = false,
  allowSelected = false,
  disableMenuOptions = false,
  disableClientDetailsModal = false,
  onSelectClientAndCoverage,
  clientUidFilter = null,

  skipClientSelection = false,
  onSkipClientSelection,
  isUpdateJob = false,
  isCreateJob = false,
  jobId = null,
  onUpdateJob: onUpdateJobProp,
  updateJobLabel = "Update Job",
  onUpdated,
  sx,
  contact = null,
  showContactsToggle = false,
  onLinkContact,
  initialActiveTable = "clients",
  paginationAlign = "right",
}) => {
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [detailsClient, setDetailsClient] = useState(null);
  const [activeTable, setActiveTable] = useState(initialActiveTable || "clients");

  const detailsTriggerRef = useRef(null);
  const lastFilterRef = useRef(null);

  const { closeByKey } = useModals();
  const { showSnackbar } = useGlobalSnackbar();

  const { mutate: patchJob, isPending: isLoading } = usePatchJob();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Search
  const { filterBy, setFilterBy, searchValue, debouncedSearch, handleSearchChange, handleClearSearch, searchInputRef } = useClientSearch({
    initialFilterBy: "clientName",
    debounceMs: 700,
  });

  // Pagination
  const { page, pageSize, handlePageChange, handlePageSizeChange, setPageToFirst } = useClientPagination({
    initialPage: 0,
    initialPageSize: 10,
  });

  // Sorting
  const [order, setOrder] = useState("ASC");
  const [orderByColumn, setOrderByColumn] = useState("");

  const queryClient = useQueryClient();

  const {
    clients,
    loading: isLoadingClients,
    error: clientsError,
    getAllClients,
    removeClient,
    editClient,
    editClientCoverageAreas,
    linkContact,
  } = useClients();

  const removeClientHandler = async (uid) => {
    await removeClient(uid);
  };

  const updateClientHandler = async (uid, payload) => {
    await editClient(uid, payload);
    getAllClients({
      page: page + 1,
      pageSize,
      orderBy: order,
      orderField: orderByColumn || "createdAt",
      search: debouncedSearch || undefined,
      filterBy: debouncedSearch ? filterBy : undefined,
      status: activeTable === "clients" ? "ACTIVE" : "PROSPECT",
    });
  };

  const handleLinkContact = async (client, contact) => {
    if (onLinkContact) {
      const res = await onLinkContact(client, contact);
      return res;
    }
    const res = await linkContact(client, contact);
    await invalidateContactsAndClients(queryClient);
    closeContactModalKeys(closeByKey);

    return res;
  };

  const openDetails = (client) => {
    if (disableRecordClickable || disableClientDetailsModal) return;
    setDetailsClient(client);
    requestAnimationFrame(() => detailsTriggerRef.current?.click());
  };

  const bulkImportMutation = useBulkImportClientsMutation({
    onSuccess: async () => {
      await getAllClients({ page: page + 1, pageSize });
      showSnackbar("Clients imported successfully", "success");
      closeByKey("Dashboard-Client-Import");
    },
    onError: (error) => {
      console.error("Error importing clients", error);
    },
  });

  const handleConfirmImport = async (parsedData) => {
    bulkImportMutation.reset();
    await bulkImportMutation.mutateAsync({
      clients: parsedData,
      status: activeTable === "prospects" ? "PROSPECT" : "ACTIVE",
    });
  };

  const handleSortColumn = (column) => {
    const isAsc = orderByColumn === column && order === "ASC";
    setOrder(isAsc ? "DESC" : "ASC");
    setOrderByColumn(column);
  };

  // Effect to load clients
  useEffect(() => {
    getAllClients({
      page: page + 1,
      pageSize,
      orderBy: order,
      orderField: orderByColumn || "createdAt",
      search: debouncedSearch || undefined,
      filterBy: debouncedSearch ? filterBy : undefined,
      status: activeTable === "clients" ? "ACTIVE" : "PROSPECT",
    });
  }, [page, pageSize, debouncedSearch, filterBy, order, orderByColumn, activeTable]);

  // Restore focus to the search input after pagination loading completes
  useEffect(() => {
    if (!isLoadingClients && searchInputRef?.current) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [isLoadingClients, searchInputRef]);

  // Reset to first page whenever search terms change
  useEffect(() => {
    setPageToFirst();
  }, [debouncedSearch, filterBy]);

  if (clientsError) return <p>Error loading clients...</p>;
  const handleClientCreated = (newClient) => {
    const selectedCoverageArea = newClient?.coverageArea ?? newClient?.coverageAreas?.[0] ?? null;

    onSelectClientAndCoverage?.({
      ...newClient,
      coverageArea: selectedCoverageArea,
    });
  };
  const filteredRows = clientUidFilter ? (clients?.items?.filter((c) => c.uid === clientUidFilter) ?? []) : (clients?.items ?? []);
  const selectedClientRow =
    clientUidFilter && selectedClient && typeof selectedClient === "object" && selectedClient.uid === clientUidFilter ? selectedClient : null;
  const visibleRows = clientUidFilter && filteredRows.length === 0 && selectedClientRow ? [selectedClientRow] : filteredRows;

  const handleUpdateJob = () => {
    if (onUpdateJobProp) {
      onUpdateJobProp();
      return;
    }

    if (jobId == null || !selectedClient?.uid) {
      return;
    }

    const rawCoverageAreaId = selectedClient.coverageArea?.id ?? selectedClient.coverageAreas?.[0]?.id;

    const coverageAreaId = rawCoverageAreaId != null ? Number(rawCoverageAreaId) : undefined;

    const payload = {
      clientUid: selectedClient.uid,
      ...(Number.isFinite(coverageAreaId) ? { coverageAreaId } : {}),
    };

    patchJob(
      {
        jobId,
        payload,
      },
      {
        onSuccess: async () => {
          await onUpdated?.();
          closeByKey(`Job-Add-Client-${jobId}`);
        },
      },
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        ...sx,
      }}
    >
      {showContactsToggle && !contact && !isCreateJob && !isUpdateJob && (
        <Tabs
          value={activeTable}
          onChange={(_, val) => setActiveTable(val)}
          sx={{
            mb: 2,
            px: 3,
            width: "100%",
            flexShrink: 0,
            borderBottom: `1px solid ${alphaVar(ORANGE.base, 0.1)}`,
            alignSelf: "stretch",
            "& .MuiTab-root": {
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.9375rem",
              color: GRAY[600],
            },
            "& .MuiTab-root.Mui-selected": {
              color: ORANGE.base,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: ORANGE.base,
            },
          }}
        >
          <Tab label="Clients" value="clients" />
          <Tab label="Prospects" value="prospects" />
        </Tabs>
      )}

      <Box sx={{ width: "100%", position: "relative", display: "flex", pt: 2, flexDirection: "column", flex: 1, minHeight: 0 }}>
        {clientUidFilter && isUpdateJob && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mb: 2,
              px: 3,
            }}
          >
            <Button
              variant="contained"
              disableElevation
              disabled={!selectedClient?.uid || isLoading}
              onClick={handleUpdateJob}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, height: 40 }}
            >
              {updateJobLabel}
            </Button>
          </Box>
        )}

        {!clientUidFilter && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" }, // ← stack on mobile
              gap: { xs: 1, sm: 1 },
              alignItems: { xs: "stretch", sm: "center" },
              mb: 2,
              width: "100%",
              justifyContent: "space-between",
              paddingLeft: 3,
              paddingRight: 3,
            }}
          >
            <ClientSearchBar
              filterBy={filterBy}
              setFilterBy={setFilterBy}
              searchValue={searchValue}
              handleSearchChange={handleSearchChange}
              handleClearSearch={handleClearSearch}
              searchInputRef={searchInputRef}
              setPageToFirst={setPageToFirst}
              isMobile={isMobile}
              activeTable={activeTable}
            />
            <ClientTableActions
              onConfirmImport={handleConfirmImport}
              bulkImportMutation={bulkImportMutation}
              skipClientSelection={skipClientSelection}
              onSkipClientSelection={onSkipClientSelection}
              isUpdateJob={isUpdateJob}
              isCreateJob={isCreateJob}
              selectedClient={selectedClient}
              onUpdateJob={handleUpdateJob}
              onClientCreated={handleClientCreated}
              updateJobLabel={updateJobLabel}
              contact={contact}
              activeTable={activeTable}
            />
          </Box>
        )}

        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            border: `1px solid rgba(12,12,12,.06)`,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(12,12,12,.04)",
            position: "relative",
            minHeight: "60vh",
            overflow: "auto",
            paddingLeft: 3,
            paddingRight: 3,
          }}
        >
          <PaginationLoadingOverlay isVisible={isLoadingClients} />

          <Box sx={{ flex: 1, overflow: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                    "& th": {
                      fontWeight: 400,
                      color: GRAY[600],
                      borderBottom: `1px solid ${alphaVar(ORANGE.base, 0.2)}`,
                      fontSize: "0.8125rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    },
                  }}
                >
                  <TableCell sx={{ width: isMobile ? 40 : 52, display: "table-cell", p: 0 }} />

                  {TABLE_COLUMNS.map((col) => (
                    <TableCell
                      key={col.label}
                      sx={{
                        display: col.hideOnMobile ? { xs: "none", sm: "table-cell" } : "table-cell",
                        pl: col.label === "Client" && isMobile ? 0 : 2,
                        width: col.label === "Client" ? { sm: "170px" } : "auto",
                      }}
                    >
                      {col.sortKey ? (
                        <TableSortLabel
                          active={orderByColumn === col.sortKey}
                          direction={orderByColumn === col.sortKey ? order : "asc"}
                          onClick={() => handleSortColumn(col.sortKey)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}

                  {activeTable === "prospects" && (
                    <TableCell align="right" sx={{ width: 150 }}>
                      Convert
                    </TableCell>
                  )}

                  {contact && (
                    <TableCell align="right" sx={{ width: 100 }}>
                      Link Phone
                    </TableCell>
                  )}

                  {!disableMenuOptions && (
                    <TableCell align="right" sx={{ width: 64 }}>
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleRows.map((clientItem) => (
                  <ClientsRow
                    key={clientItem.uid}
                    row={clientItem}
                    selectedClient={selectedClient}
                    disableInnerChilds={disableInnerChilds}
                    allowSelected={allowSelected}
                    disableMenuOptions={disableMenuOptions}
                    disableRecordClickable={disableRecordClickable}
                    handleDelete={removeClientHandler}
                    handleUpdate={updateClientHandler}
                    linkContactToClient={handleLinkContact}
                    onSelectClientAndCoverage={onSelectClientAndCoverage}
                    handleUpdateCoverageArea={editClientCoverageAreas}
                    onOpenModalDetails={openDetails}
                    contact={contact}
                    activeTable={activeTable}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>

          {!clientUidFilter && (
            <TablePagination
              component="div"
              count={clients?.totalItems || visibleRows.length || 0}
              rowsPerPage={pageSize}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handlePageSizeChange}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                borderTop: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
                "& .MuiTablePagination-spacer": {
                  display: paginationAlign === "left" ? "none !important" : "block",
                  flex: paginationAlign === "left" ? "none !important" : "1 1 100%",
                  width: paginationAlign === "left" ? "0 !important" : "auto",
                },
                "& .MuiTablePagination-toolbar": {
                  justifyContent: paginationAlign === "left" ? "flex-start !important" : "flex-end !important",
                  paddingLeft: paginationAlign === "left" ? "0 !important" : undefined,
                },
                "& .MuiTablePagination-actions": {
                  marginLeft: paginationAlign === "left" ? "16px !important" : "20px",
                },
              }}
            />
          )}
        </TableContainer>
      </Box>

      {!disableClientDetailsModal && (
        <BaseModalComponent
          modalKey="Client-Details-Modal"
          modalLabel="Client Details"
          defaultSizes={{ width: 700 }}
          elementTrigger={<button ref={detailsTriggerRef} type="button" style={{ position: "absolute", visibility: "hidden" }} />}
        >
          {detailsClient && !disableRecordClickable && <ClientDetailsTabs client={detailsClient} />}
        </BaseModalComponent>
      )}
    </Box>
  );
};
